import { useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../context/AuthContext.shared';
import { setAccountAnalyticsBlocked, revokeServerAnalytics } from '../services/analytics';
import { notifyCookiePreferences } from './useCookiePreferences';
import { COOKIE_CONSENT_VERSION, COOKIE_PREFERENCES_KEY, readCookieChoice, writeCookieChoice, anonymousConsentId, clearLegacyCookieConsent, hasLegacyRefusal } from '../services/cookieConsent';

interface Preferences {
    userId: string | null;
    loading: boolean;
    saving: boolean;
    marketingMeasurement: boolean;
    personalizedMarketing: boolean;
    needsReconsent: boolean;
    error: string | null;
}
let snapshot: Preferences = { userId: null, loading: true, saving: false, marketingMeasurement: false,
    personalizedMarketing: false, needsReconsent: true, error: null };
let revision = 0;
const listeners = new Set<() => void>();
const publish = (patch: Partial<Preferences>) => { snapshot = { ...snapshot, ...patch }; listeners.forEach(fn => fn()); };
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
interface ServerChoice { marketing_measurement: boolean; personalized_marketing: boolean; needs_reconsent: boolean; consent_version: string }
const request = async (method: string, body?: object, guest = false): Promise<ServerChoice> => {
    const response = await fetch('/api/account/cookie-preferences' + (guest ? '/anonymous' : ''), {
        method, credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(8000),
        headers: { ...(body ? { 'Content-Type': 'application/json' } : {}),
            ...(!guest && snapshot.userId ? { 'X-Cookie-Preference-Owner': snapshot.userId } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw Error('Preferințele nu au putut fi confirmate. Încearcă din nou.');
    return response.json();
};
const payload = (measurement: boolean, personalized: boolean, source: string) => ({
    strictly_necessary: true, marketing_measurement: measurement, personalized_marketing: personalized,
    consent_version: COOKIE_CONSENT_VERSION, source, anonymous_consent_id: anonymousConsentId(),
});

export const loadAccountCookiePreferences = async (userId: string | null) => {
    const current = ++revision;
    setAccountAnalyticsBlocked(null);
    publish({ userId, loading: true, saving: false, error: null });
    let local = readCookieChoice();
    // Session expiry does not revoke a browser choice. Retain its owner so a
    // different signed-in account never inherits it or submits it as its own.
    if (userId && local?.owner && local.owner !== userId) { writeCookieChoice(null); clearLegacyCookieConsent(); local = null; }
    try {
        if (!userId) {
            if (local?.pending && !local.owner) {
                try {
                    await request('POST', payload(local.marketingMeasurement, local.personalizedMarketing, 'banner'), true);
                    if (current !== revision) return;
                    local = { ...local, pending: false };
                    writeCookieChoice(local);
                } catch {
                    local = { ...local, pending: false };
                    writeCookieChoice(local);
                }
            }
            publish({ loading: false, marketingMeasurement: !!local?.marketingMeasurement,
                personalizedMarketing: !!local?.personalizedMarketing, needsReconsent: !local });
            setAccountAnalyticsBlocked(false);
            return;
        }
        let server = await request('GET');
        if (current !== revision) return;
        if (server.consent_version !== COOKIE_CONSENT_VERSION) throw Error('Versiunea preferințelor s-a schimbat. Reîncarcă pagina.');
        if (!local && hasLegacyRefusal() && !server.needs_reconsent) {
            server = await request('PUT', payload(false, false, 'account_sync'));
            if (current !== revision) return;
            clearLegacyCookieConsent();
        }
        if (local && (local.owner === null || local.pending)) {
            const measurement = local.marketingMeasurement && (server.needs_reconsent || server.marketing_measurement);
            const personalized = local.personalizedMarketing && (server.needs_reconsent || server.personalized_marketing);
            server = await request('PUT', payload(measurement, personalized, 'account_sync'));
            if (current !== revision) return;
        }
        if (server.needs_reconsent) writeCookieChoice(null);
        else writeCookieChoice({ version: COOKIE_CONSENT_VERSION, marketingMeasurement: server.marketing_measurement,
            personalizedMarketing: server.personalized_marketing, savedAt: new Date().toISOString(), owner: userId });
        publish({ loading: false, marketingMeasurement: server.marketing_measurement,
            personalizedMarketing: server.personalized_marketing, needsReconsent: server.needs_reconsent });
        setAccountAnalyticsBlocked(false);
    } catch (error) {
        if (current !== revision) return;
        setAccountAnalyticsBlocked(true);
        publish({ loading: false, error: error instanceof Error ? error.message : 'Preferințele nu au putut fi încărcate.' });
    }
};

export const saveAccountCookiePreferences = async (measurement: boolean, personalized: boolean, source = 'settings') => {
    if (snapshot.loading || snapshot.saving) return false;
    const current = ++revision;
    const owner = snapshot.userId;
    const previous = readCookieChoice();
    publish({ saving: true, error: null });
    // Revocation is immediate; a new grant waits for server acknowledgement.
    writeCookieChoice({ version: COOKIE_CONSENT_VERSION, marketingMeasurement: measurement && !!previous?.marketingMeasurement,
        personalizedMarketing: personalized && !!previous?.personalizedMarketing, savedAt: new Date().toISOString(), owner, pending: true });
    notifyCookiePreferences();
    if (!measurement) revokeServerAnalytics();
    try {
        const server = await request(owner ? 'PUT' : 'POST', payload(measurement,personalized,source), !owner);
        if (current !== revision) return false;
        if (owner && server.consent_version !== COOKIE_CONSENT_VERSION) throw Error('Versiune incompatibilă.');
        writeCookieChoice({ version: COOKIE_CONSENT_VERSION, marketingMeasurement: measurement,
            personalizedMarketing: personalized, savedAt: new Date().toISOString(), owner });
        clearLegacyCookieConsent();
        publish({ saving: false, marketingMeasurement: measurement, personalizedMarketing: personalized, needsReconsent: false });
        setAccountAnalyticsBlocked(false);
        return true;
    } catch {
        if (current === revision) publish({ saving: false, error: 'Alegerea nu a fost confirmată pe server. Opțiunile retrase rămân oprite în acest browser. Reîncearcă salvarea.' });
        return false;
    }
};
export const useAccountCookiePreferences = () => useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
export const useSyncAccountCookiePreferences = () => {
    const { user, isLoading } = useAuth();
    useEffect(() => {
        const sync = (event: StorageEvent) => {
            if (event.key !== COOKIE_PREFERENCES_KEY) return;
            const local = readCookieChoice();
            if (local?.pending) {
                // Another tab owns this in-flight save. Observe immediate denials,
                // but never submit its temporary intersection back to the server.
                notifyCookiePreferences();
                return;
            }
            void loadAccountCookiePreferences(user?.id ?? null);
        };
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, [user?.id]);
    useEffect(() => {
        if (!isLoading) void loadAccountCookiePreferences(user?.id ?? null);
        return () => { revision += 1; };
    }, [user?.id, isLoading]);
};
